import {
  FocusEvent,
  KeyboardEvent,
  useCallback,
  useRef,
  useState,
  useMemo,
  ReactNode,
} from 'react';
import { CreatableProps } from 'react-select/creatable';
import {
  components,
  FormatOptionLabelMeta,
  SelectInstance,
  GroupBase,
  OnChangeValue,
} from 'react-select';

import { ENTER_KEYCODE } from 'lib/strings/strings';
import stringToArray from 'lib/logic/string-to-array';
import { PStyled, SmallStyled } from 'approot/shared/styles/typography.style';
import IconSearch from 'approot/shared/icons/icon-search';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import {
  ListSearchHighlightedText,
  ListSearchStyled,
} from './list-search.style';

const getHighlightedText = (text: string, highlight: string): ReactNode => {
  if (!highlight.trim()) {
    return text;
  }
  const regex = new RegExp(`(${highlight})`, 'gi');
  const parts = text.split(regex);
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <ListSearchHighlightedText key={i}>{part}</ListSearchHighlightedText>
        ) : (
          part
        )
      )}
    </>
  );
};

export type ListSearchItem = {
  id: number;
  label: string;
  description?: string;
};

type Props = Omit<
  CreatableProps<ListSearchItem, false, GroupBase<ListSearchItem>>,
  'options'
> & {
  options: ListSearchItem[];
  values?: ListSearchItem[] | undefined;
  minCharsToFilter?: number; // New prop to define minimum characters before filtering starts
  tooltip?: string;
  selectedOptionValues?: ListSearchItem[];
  onSelect?: (value: ListSearchItem) => void;
  onInputChange?: (value: string) => void;
  onSubmitNoSelection?: () => void;
  inputId?: string;
  fieldName?: string;
  allowFormSubmissionOnEnter?: boolean;
  noOptionsComponent?: ReactNode;
  showAllOptionsInitially?: boolean;
  // handleChange receives a mixed array of existing ListSearchItems plus
  // raw strings parsed from free-typed comma-separated input.
  handleChange?: (values: any) => void;
  /** When set, dropdown rows use this instead of plain label + substring highlight (e.g. KaTeX). */
  formatMenuOptionLabel?: (
    option: ListSearchItem,
    inputValue: string
  ) => ReactNode;
};

const DropdownIndicator = (props: any) => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        <IconSearch width={16} height={16} />
      </components.DropdownIndicator>
    )
  );
};

const ListSearch = ({
  options,
  selectedOptionValues,
  values,
  handleChange,
  minCharsToFilter = 1, // Default to 1 character,
  showAllOptionsInitially = true,
  tooltip,
  onSelect,
  onInputChange,
  onSubmitNoSelection,
  inputId,
  noOptionsComponent,
  formatMenuOptionLabel,
  ...props
}: Props) => {
  const creatableRef = useRef<SelectInstance<ListSearchItem, false> | null>(
    null
  );
  const [inputValue, setInputValue] = useState<string>('');

  const renderOptionsLabel = useCallback(
    (
      option: ListSearchItem,
      labelMeta: FormatOptionLabelMeta<ListSearchItem>
    ) => {
      if (labelMeta.context === 'menu' && formatMenuOptionLabel) {
        const description =
          option.description && labelMeta.inputValue
            ? getHighlightedText(option.description, labelMeta.inputValue)
            : option.description;
        return (
          <>
            <PStyled marginBottom={0}>
              {formatMenuOptionLabel(option, labelMeta.inputValue)}
            </PStyled>
            {option.description && <SmallStyled>{description}</SmallStyled>}
          </>
        );
      }
      if (labelMeta.context === 'menu') {
        const label = labelMeta.inputValue
          ? getHighlightedText(option.label, labelMeta.inputValue)
          : option.label;
        const description =
          option.description && labelMeta.inputValue
            ? getHighlightedText(option.description, labelMeta.inputValue)
            : option.description;
        return (
          <>
            <PStyled marginBottom={0}>{label}</PStyled>
            {option.description && <SmallStyled>{description}</SmallStyled>}
          </>
        );
      }
      return option.label;
    },
    [formatMenuOptionLabel]
  );

  const currentValue = values;

  // Filter options based on input value and minimum character threshold
  const filteredOptions = useMemo(() => {
    if (!inputValue || inputValue.length < minCharsToFilter) {
      return showAllOptionsInitially ? options : [];
    }

    const lowerInputValue = inputValue.toLowerCase();

    const filtered = options.filter(option => {
      // Filter by code
      if (option.label.toLowerCase().includes(lowerInputValue)) {
        return true;
      }

      // Filter by description if it exists
      if (
        option.description &&
        option.description.toLowerCase().includes(lowerInputValue)
      ) {
        return true;
      }

      return false;
    });

    return filtered;
  }, [inputValue, minCharsToFilter, options, showAllOptionsInitially]);

  const handleInputChange = useCallback(
    (newValue: string, actionMeta: any) => {
      // Only update input value if it's user input (not programmatic)
      if (actionMeta.action === 'input-change') {
        setInputValue(newValue);
        onInputChange?.(newValue);
      }
    },
    [onInputChange]
  );

  // Create a memoized noOptionsMessage function to avoid recreating on every render
  const noOptionsMessage = useMemo(() => {
    return () => {
      // Check if the input exactly matches any selected option
      const exactMatch = selectedOptionValues?.find(
        option => option.label.toLowerCase() === inputValue.toLowerCase()
      );

      if (exactMatch) {
        return "That option's already on your list. Try another";
      }

      if (inputValue.length >= minCharsToFilter) {
        return noOptionsComponent || 'Nothing found. Want to try again?';
      }

      return 'Type to search...';
    };
  }, [selectedOptionValues, inputValue, minCharsToFilter, noOptionsComponent]);

  const handleOnBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (event.target.value.length > 0) {
        event.preventDefault();
        const values = stringToArray(event.target.value);

        const allValues = [...(currentValue || []), ...values];
        handleChange && handleChange(allValues);
      }
    },
    [currentValue, handleChange]
  );

  const handleOnChange = useCallback(
    (value: OnChangeValue<ListSearchItem, false>) => {
      // Clear the input field when a selection is made (single select behavior)
      setInputValue('');
      if (value) {
        onSelect && onSelect(value);
      }
    },
    [onSelect]
  );

  const handleOnKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (
        event.keyCode === ENTER_KEYCODE &&
        creatableRef.current?.state.focusedOption == null
      ) {
        // no menu item is highlighted
        if (onSubmitNoSelection) {
          event.preventDefault();
          onSubmitNoSelection();
        }
        (event.target as HTMLInputElement).blur();
        (event.target as HTMLInputElement).focus();
      }
    },
    [onSubmitNoSelection]
  );

  const content = (
    <ListSearchStyled
      ref={creatableRef}
      value={null} // Always null to keep input clear - this is a search field, not a value display
      onChange={handleOnChange}
      onBlur={handleOnBlur}
      options={filteredOptions} // Use filtered options instead of all options
      onInputChange={handleInputChange} // Handle input changes for filtering
      inputValue={inputValue} // Control the input value
      filterOption={() => true} // Disable react-select's built-in filtering since we handle it ourselves
      components={{
        DropdownIndicator,
        IndicatorSeparator: () => null, // Remove the separator line
      }}
      onKeyDown={(e: KeyboardEvent<HTMLElement>) => handleOnKeyDown(e)}
      isClearable={false}
      isSearchable
      menuShouldScrollIntoView={false}
      classNamePrefix="Select"
      blurInputOnSelect={true} // Blur the input when a selection is made (single select behavior)
      isValidNewOption={() => false} // turns off ability to add new options
      noOptionsMessage={noOptionsMessage}
      formatOptionLabel={renderOptionsLabel}
      inputId={inputId}
      {...props}
    />
  );
  if (tooltip) {
    return (
      <TooltipWrapper hoverText={tooltip} multiline>
        {content}
      </TooltipWrapper>
    );
  }
  return content;
};

export default ListSearch;
